import { useMemo } from 'react';

import type { ItemDragAndDropGroup } from '@atlassian/navigation-drag-and-drop';

import { useItemsAndShortcuts } from '../../../../controllers/items-and-shortcuts-provider';
import { useItemProperties } from '../../../../controllers/items-and-shortcuts-provider/use-item-properties';
import { useUserMenuDataShapes } from '../../../../controllers/user-state/user-menu-data-shapes-provider';
import { getMenuIdsByReorderable } from '../../../utils/get-menu-ids-by-reorderable';

export function useNavItemReorderingContext(itemMenuId: string, group?: ItemDragAndDropGroup) {
	const { allItems, shortcuts = {} } = useItemsAndShortcuts();
	const [reorderableIds] = getMenuIdsByReorderable({ allItems, shortcuts, group });
	const { visibleMenuIds } = useUserMenuDataShapes();
	const { getItemProperties } = useItemProperties();

	const visibleAndReorderableIds = useMemo(() => {
		return visibleMenuIds.filter((id) => reorderableIds.includes(id));
	}, [reorderableIds, visibleMenuIds]);
	const itemIndex = useMemo(
		() => visibleAndReorderableIds.indexOf(itemMenuId),
		[visibleAndReorderableIds, itemMenuId],
	);
	const moveUpPosition = useMemo<number | false>(() => {
		// Not visible and reorderable, can't move
		if (itemIndex === -1) {
			return false;
		}
		// Already the first reorderable item, can't move up
		if (itemIndex === 0) {
			return false;
		}

		const previousVisibleReorderableItem = visibleAndReorderableIds[itemIndex - 1];
		// Get and return the index of the previous visible reorderable item in the whole menu
		const previousItem = getItemProperties(previousVisibleReorderableItem);
		return previousItem?.index ?? false;
	}, [getItemProperties, itemIndex, visibleAndReorderableIds]);

	const moveDownPosition = useMemo<number | false>(() => {
		// Not visible and reorderable, can't move
		if (itemIndex === -1) {
			return false;
		}
		// Already the last reorderable item, can't move down
		if (itemIndex === visibleAndReorderableIds.length - 1) {
			return false;
		}

		const nextVisibleReorderableItem = visibleAndReorderableIds[itemIndex + 1];
		// Get and return the index of the next visible reorderable item in the whole menu
		const nextItem = getItemProperties(nextVisibleReorderableItem);
		return nextItem?.index ?? false;
	}, [getItemProperties, itemIndex, visibleAndReorderableIds]);

	const menuTopPosition = useMemo<number | false>(() => {
		// Get the index of the first visible reorderable item in the whole menu
		const firstVisibleReorderableItem = visibleAndReorderableIds[0];
		const firstItem = getItemProperties(firstVisibleReorderableItem);
		return firstItem?.index ?? false;
	}, [getItemProperties, visibleAndReorderableIds]);

	const menuBottomPosition = useMemo<number | false>(() => {
		// Get the index of the last visible reorderable item in the whole menu
		const lastVisibleReorderableItem =
			visibleAndReorderableIds[visibleAndReorderableIds.length - 1];
		const lastItem = getItemProperties(lastVisibleReorderableItem);
		return lastItem?.index ?? false;
	}, [getItemProperties, visibleAndReorderableIds]);

	return {
		index: getItemProperties(itemMenuId)?.index,
		moveUpPosition,
		moveDownPosition,
		menuTopPosition,
		menuBottomPosition,
	};
}
